import {FC, RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {PropertyCarousel} from '@components/PropertyCarousel';
import {CarouselNav} from '@components/CarouselNav';
import {Icon} from '@components/Icon';
import {VisuallyHidden} from '@components/VisuallyHidden';
import {FormattedMessage} from 'react-intl';
import {Image} from '@components/Image';
import {useCarousel} from '@hooks/useCarousel';
import NextImage, {StaticImageData} from 'next/image';
import {GalleryLightbox} from '@components/GalleryLightbox';
import {Modal} from '@components/Modal';
import {Typography} from '@components/Typography';
import {Row} from '@components/Row';
import {Col} from '@components/Col';
import {MortgagePromoCard} from '@liveComponents/PromoCard/Mortgage';
import {AdvertMessageType, Currency, Scalars} from '.cache/__types__';
import {SendMessageButtonProps} from '@pageComponents/Detail/SendMessageButton';
import {getImageSizeForMaxSize} from '@utils/image';
import {Container} from '@components/Container';
import {useMQ} from '@hooks/useMQ';
import {ContentBox} from '@components/ContentBox';
import {Button} from '@components/Button';

type ImageData = {
    id: Scalars['ID'];
    size?: {
        width: number;
        height: number;
    };
    url: string;
};

export type CarouselProps = {
    id: Scalars['ID'];
    images: ImageData[] | StaticImageData;
    messageData?: SendMessageButtonProps['messageData'];
    variant?: 'simple' | 'classic';
    sendMessageButtonRef?: RefObject<HTMLButtonElement>;
    price?: number;
    percentageRate?: number;
    currency?: Currency;
};

export const Carousel: FC<CarouselProps> = ({
    id,
    images,
    messageData,
    variant = 'classic',
    sendMessageButtonRef,
    price,
    percentageRate,
    currency,
}) => {
    const {emblaRef, embla, prevBtnDisabled, nextBtnDisabled, scrollPrev, scrollNext} = useCarousel({
        align: variant === 'simple' ? 'start' : 'center',
    });
    const lazyRef = useRef(null);
    const [lightboxController, setLightboxController] = useState({
        toggler: false,
        sourceIndex: 0,
    });
    const [renderedAdvert, setRenderedAdvert] = useState(id);
    const isMobile = useMQ('mdDown');

    const goToLightboxSlide = useCallback((index) => {
        setLightboxController((prevState) => ({
            ...prevState,
            sourceIndex: index,
        }));
    }, []);

    const openLightboxOnSlide = useCallback(
        (index) => {
            if (embla && embla.clickAllowed()) {
                setLightboxController({
                    toggler: !lightboxController.toggler,
                    sourceIndex: index,
                });
            }
        },
        [embla, lightboxController.toggler],
    );

    useEffect(() => {
        if (embla && (embla.slideNodes().length !== (Array.isArray(images) ? images : [images]).length || renderedAdvert !== id)) {
            embla.reInit();
            embla.scrollTo(0);
            goToLightboxSlide(0);
        }
        if (renderedAdvert !== id) {
            setRenderedAdvert(id);
        }
    }, [embla, images, id, renderedAdvert, goToLightboxSlide]);

    const closeLightbox = useCallback(() => {
        setLightboxController({
            ...lightboxController,
            toggler: false,
        });
    }, [lightboxController]);

    const lastGalleryLightboxSlideContent = useMemo(
        () => (
            <Modal.Dialog size="lg" scrollable>
                <Modal.Body>
                    <Typography as="h3" textAlign="center" className="mb-4 mb-sm-10">
                        <FormattedMessage defaultMessage={'Zaujala vás tato nemovitost?'} />
                    </Typography>

                    <Row className="g-3 mb-4 mb-sm-10">
                        {Array.isArray(images) &&
                            images.map((image, idx) => {
                                const size = getImageSizeForMaxSize(image?.size ?? {width: 1800, height: 1200}, {
                                    width: 150,
                                    height: 100,
                                });
                                return (
                                    <Col xs={6} sm={4} lg={4} key={image.id}>
                                        <a
                                            href={image.url}
                                            onClick={(event) => {
                                                event.preventDefault();

                                                goToLightboxSlide(idx);
                                            }}
                                        >
                                            <ContentBox className="p-2 position-relative" rounded="sm" outline>
                                                <Image>
                                                    <NextImage src={image.url} width={size?.width ?? 750} height={size?.height ?? 500} />
                                                </Image>
                                            </ContentBox>
                                        </a>
                                    </Col>
                                );
                            })}
                    </Row>

                    {messageData && messageData.messageType !== AdvertMessageType.Inactive && sendMessageButtonRef?.current && (
                        <Row className="justify-content-center">
                            <Col xs={12} md="auto">
                                <Button
                                    onClick={() => {
                                        closeLightbox();
                                        sendMessageButtonRef.current?.click();
                                    }}
                                >
                                    {messageData.messageType !== AdvertMessageType.Lead && (
                                        <FormattedMessage defaultMessage={'Kontaktovat majitele'} />
                                    )}

                                    {messageData.messageType === AdvertMessageType.Lead && (
                                        <FormattedMessage defaultMessage={'Reagovat na inzerát'} />
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    )}
                </Modal.Body>
            </Modal.Dialog>
        ),
        [closeLightbox, messageData, images, goToLightboxSlide, sendMessageButtonRef],
    );

    return (
        <>
            {Array.isArray(images) && (
                <PropertyCarousel
                    ref={lazyRef}
                    variant={variant === 'simple' ? 'simple' : undefined}
                    emblaRef={emblaRef}
                    navButtons={
                        (!prevBtnDisabled || !nextBtnDisabled) && (
                            <CarouselNav size={variant === 'simple' ? 'sm' : undefined}>
                                <CarouselNav.Button onClick={scrollPrev} disabled={prevBtnDisabled}>
                                    <Icon.ArrowLeft position="middle" />
                                    <VisuallyHidden>
                                        <FormattedMessage defaultMessage="Předchozí" />
                                    </VisuallyHidden>
                                </CarouselNav.Button>
                                <CarouselNav.Button onClick={scrollNext} disabled={nextBtnDisabled}>
                                    <Icon.ArrowRight position="middle" />
                                    <VisuallyHidden>
                                        <FormattedMessage defaultMessage="Následující" />
                                    </VisuallyHidden>
                                </CarouselNav.Button>
                            </CarouselNav>
                        )
                    }
                >
                    {images
                        .filter((image) => image.url !== '')
                        .map((image, idx) => {
                            const width = ((image.size?.width ?? 1800) / (image.size?.height ?? 1200)) * 500;
                            const smallWidth = ((image.size?.width ?? 1800) / (image.size?.height ?? 1200)) * 300;

                            return (
                                <PropertyCarousel.Slide key={image.id}>
                                    {variant !== 'simple' ? (
                                        <a
                                            href={image.url}
                                            onClick={(event) => {
                                                event.preventDefault();

                                                openLightboxOnSlide(idx);
                                            }}
                                        >
                                            <Image>
                                                <PropertyCarousel.CanvasPlaceholder
                                                    width={width}
                                                    height={500}
                                                />
                                                <NextImage
                                                    src={image.url}
                                                    lazyRoot={lazyRef}
                                                    lazyBoundary={'750px'}
                                                    layout={'fill'}
                                                    sizes={`(min-width: 768px) ${width}px, ${smallWidth}px`}
                                                    objectFit={'cover'}
                                                    priority={idx < 3}
                                                />
                                            </Image>
                                        </a>
                                    ) : (
                                        <Image>
                                            <PropertyCarousel.CanvasPlaceholder
                                                width={((image.size?.width ?? 1800) / (image.size?.height ?? 1200)) * 500}
                                                height={500}
                                            />
                                            <NextImage
                                                src={image.url}
                                                lazyRoot={lazyRef}
                                                lazyBoundary={'500px'}
                                                sizes={`(min-width: 768px) ${width}px, ${smallWidth}px`}
                                                layout={'fill'}
                                                objectFit="cover"
                                            />
                                        </Image>
                                    )}
                                </PropertyCarousel.Slide>
                            )
                        })}
                </PropertyCarousel>
            )}

            {!Array.isArray(images) && (
                <Container variant={'wide'}>
                    <div className={'position-relative'} style={{minHeight: isMobile ? '300px' : '500px'}}>
                        <Image className={'text-center'} layout={'fill'}>
                            <NextImage src={images} layout={'fill'} objectFit={'cover'} />
                        </Image>
                    </div>
                </Container>
            )}

            {Array.isArray(images) && variant !== 'simple' && (
                <GalleryLightbox
                    {...lightboxController}
                    type="image"
                    promoBox={
                        price && percentageRate && currency ? (
                            <MortgagePromoCard price={price} percentageRate={percentageRate} currency={currency} />
                        ) : undefined
                    }
                    sources={[
                        ...images.map((image) => image.url),
                        <GalleryLightbox.CustomSource>{lastGalleryLightboxSlideContent}</GalleryLightbox.CustomSource>,
                    ]}
                />
            )}
        </>
    );
};
